import { EarningsPeriod } from '@api';
import {
  type AppTableFeatures,
  RevampTable,
  TableCell,
} from '@design/components/table';
import { isElementScrollbarVisible } from '@helpers';
import { Column, flexRender, Row, Table } from '@tanstack/react-table';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import {
  EARNINGS_COLUMN_ID,
  getCellHighlighted,
  PERIOD_COLUMN_ID,
  RELEASED_ON_COLUMN_ID,
  SELECTED_COLUMN_ID,
} from '../../utils/utils';
import { EarningsTableHeaderCell } from '../EarningsTableHeaderCell';
import styles from './EarningsTable.module.scss';

interface EarningsTableProps {
  table: Table<AppTableFeatures, EarningsPeriod>;
  rows: Row<AppTableFeatures, EarningsPeriod>[];
  onRowClick: (row: Row<AppTableFeatures, EarningsPeriod>) => void;
}

export const EarningsTable = ({
  table,
  rows,
  onRowClick,
}: EarningsTableProps) => {
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { resetAllFilterOptions } = useEarningsStore();
  const { t } = useTranslation();

  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );

  useEffect(
    () => setHasHorizontalScroll(isElementScrollbarVisible(containerRef)),
    [containerRef]
  );

  const isCellNumber = (columnId: string) => {
    return (
      columnId !== EARNINGS_COLUMN_ID && columnId !== RELEASED_ON_COLUMN_ID
    );
  };

  const isStartAlignedColumn = (columnId: string) =>
    [
      SELECTED_COLUMN_ID,
      EARNINGS_COLUMN_ID,
      RELEASED_ON_COLUMN_ID,
      PERIOD_COLUMN_ID,
    ].includes(columnId);

  const getIsPinnedVertically = (
    column: Column<AppTableFeatures, EarningsPeriod>
  ) => hasHorizontalScroll && !!column.getIsPinned();

  if (!rows.length) {
    return (
      <>
        {isNewEarningsTabEnabled ? (
          <p className={styles.emptyMessage}>
            {t('widget:earnings.empty.title')}
          </p>
        ) : (
          <div className={styles.errorMessageAndResetButtonWrapper}>
            <ErrorMessageWrapper
              variant="empty"
              displayIcon
              title={t('widget:earnings.empty.title')}
              subTitle={t('widget:earnings.empty.emptyFilteredResultsSubTitle')}
              showBorder={false}
              buttonProps={{
                label: t('widget:earnings.resetAll'),
                onClick: resetAllFilterOptions,
                variant: 'empty',
                size: 'small',
                iconName: 'Reset',
                className: styles.styledButton,
                'data-testid': 'earnings-reset-all-filters-button',
              }}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className={styles.tableScrollContainer} ref={containerRef}>
        <RevampTable data-testid="earnings-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index, arr) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isLastHeaderGroup = index === arr.length - 1;
                  return (
                    <EarningsTableHeaderCell
                      key={header.id}
                      header={header}
                      isLastHeaderGroup={isLastHeaderGroup}
                      isPinnedVertically={getIsPinnedVertically(header.column)}
                      isHighlighted={getCellHighlighted(header.column.id)}
                    />
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} onClick={() => onRowClick(row)}>
                {row.getVisibleCells().map(cell => {
                  const isPinned = getIsPinnedVertically(cell.column);
                  const isHighlighted = getCellHighlighted(cell.column.id);

                  return (
                    <TableCell
                      key={cell.id}
                      className={clsx(styles.styledTableCell, {
                        [styles.cellHighlighted]: isHighlighted,
                        [styles.cellPinned]: isPinned,
                        [styles.cellLastPinned]:
                          isPinned && cell.column.getIsLastColumn('start'),
                      })}
                      isNumber={isCellNumber(cell.column.id)}
                      alignment={
                        isStartAlignedColumn(cell.column.id) ? 'start' : 'end'
                      }
                      data-earnings-cell
                      style={
                        {
                          '--cell-left': isPinned
                            ? `${cell.column.getStart('start')}px`
                            : undefined,
                          '--cell-right': isPinned
                            ? `${cell.column.getAfter('end')}px`
                            : undefined,
                        } as CSSProperties
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </RevampTable>
      </div>
    </>
  );
};
